import { MARKETING_SECTION_TYPES, tt } from '@agos/shared'

/**
 * The published marketing page, as a visitor gets it.
 *
 * `GET /api/v1/marketing/{slug}` is public and serves **only** `sections` — the editor writes
 * `draft_sections` and publishing copies one over the other. The server says why that matters:
 * *"a page with no `published_at` is not found rather than empty — an unpublished page and a page that
 * does not exist are the same thing to a visitor, and saying otherwise would let anyone enumerate what
 * is being drafted."* So this file never asks for a draft, and a 404 is rendered as *nothing here*
 * rather than as a failure.
 *
 * **A section type with no renderer is skipped and counted, not crashed on.** The editor can publish a
 * type this build has never heard of — that is what shipping a new section looks like before the site is
 * redeployed. A visitor should get the rest of the page; the count exists so a screenshot pair or a
 * health check can notice.
 */

export interface Section {
  id: string
  type: string
  props: Record<string, unknown>
}

export interface SiteSettings {
  domain: string | null
  /**
   * §3.181, FB-954 - the tab icon for this page, or null for the app's own static one.
   *
   * **A missed port on both halves**: legacy's editor has a `MediaUploader` for it and legacy's `App.tsx` ends
   * its settings block by creating or updating `link[rel='icon']` from it. AGOS had neither, so §3.180's static
   * file was the only answer - which is the right **default** and was never meant to be the only one.
   *
   * **`logo` was here and is gone**, with its control and its parse: it was offered by the Tower, sanitised
   * here, and read by nothing in the whole shopfront - legacy's own §3.132 defect, ported faithfully.
   */
  faviconUrl: string | null
  /**
   * §3.182, FB-956 - the body and display faces, each one of the families `index.css` preloads.
   *
   * **This was a single `font` writing the same stack to both variables**, parsed with no check at all - so
   * the Tower's free-text box could set `Lora` or `Playfair Display`, which are **legacy's** families and not
   * AGOS's, and the page rendered in the system sans with nothing saying so.
   *
   * **`font` IS read here as the migration fallback, and the first draft of §3.182 had that wrong.** It was
   * left out to keep §3.181's guard free of exemptions, on a measurement that said the owner's page carried
   * empty settings - **and that measurement was mine and it was false**: the server's key is `siteSettings`
   * and I read `settings`, so an absent key read as an empty object. Their page carries
   * `{"domain": …, "font": "Inter", "logo": "", "primaryColor": "#a01ad1", "secondaryColor": "#e81717"}`,
   * confirmed against the live API and against `central_hub-2026-07-28`'s own row.
   *
   * So dropping the fallback here would take a **stored** face off a live page until somebody re-saved it.
   * On their row the rendered result would have been identical by coincidence - `index.css` falls back to
   * `'Inter'`, which is what they stored - and a page storing `Poppins` would have lost it. §3.181's guard
   * carries a named exemption for `font` instead, with its reason asserted.
   */
  bodyFont: string | null
  headingFont: string | null
  primaryColor: string | null
  secondaryColor: string | null
  /** §3.166 - `dark | light | warm`, or null for the stylesheet's own. */
  theme: string | null
  /** §3.166 - `sharp | medium | round`, or null. */
  radius: string | null
}

/**
 * The themes and radii `index.css` has rules for - §3.166.
 *
 * **Anything else reads as absent.** `site_settings` is an admin-authored `jsonb` blob, so a value outside
 * these sets can arrive; writing it onto the root would leave the page matching no rule while claiming a
 * choice had been made. Same reasoning as `safeAsset` below, applied to an attribute rather than a URL.
 */
export const THEMES = ['dark', 'light', 'warm']
export const RADII = ['sharp', 'medium', 'round']

/**
 * The families `index.css` preloads - §3.182, and the one list this file validates against.
 *
 * **Anything else reads as absent, for the reason above applied to a face rather than a theme**: writing an
 * unloaded family onto the root claims a choice while the browser falls through to the system sans, where an
 * absent value falls back to `--font-body`'s own `'Inter'` - which is loaded.
 */
export const FONTS = ['Inter', 'Outfit', 'Poppins', 'Roboto', 'Nunito']

export interface PublishedPage {
  slug: string
  title: string
  description: string
  sections: Section[]
  settings: SiteSettings
  publishedAt: string | null
}

/**
 * The types this build can draw. Measured on `home`: all nine are in use.
 *
 * **§3.45 - one list now, shared with the Tower's editor.** This was a second copy of
 * `KNOWN_SECTIONS`, and a type in the editor and not here publishes a section the public site
 * silently drops. Kept as a re-export under its own name so every reader here is unchanged.
 */
export const RENDERABLE = MARKETING_SECTION_TYPES

function text(row: Record<string, unknown>, key: string): string | null {
  const value = row[key]
  return typeof value === 'string' && value.trim() !== '' ? value.trim() : null
}

export function publishedPageOf(payload: unknown): PublishedPage | null {
  const outer = (typeof payload === 'object' && payload !== null ? payload : {}) as Record<string, unknown>
  const row = (typeof outer.page === 'object' && outer.page !== null ? outer.page : outer) as Record<string, unknown>
  const slug = text(row, 'slug')
  if (slug === null) return null

  const settings = (typeof row.siteSettings === 'object' && row.siteSettings !== null ? row.siteSettings : {}) as Record<string, unknown>
  const sections = Array.isArray(row.sections) ? row.sections : []

  return {
    slug,
    title: text(row, 'title') ?? '',
    description: text(row, 'description') ?? '',
    sections: sections.flatMap((entry) => {
      if (typeof entry !== 'object' || entry === null) return []
      const section = entry as Record<string, unknown>
      const type = text(section, 'type')
      if (type === null) return []
      return [
        {
          id: text(section, 'id') ?? type,
          type,
          props: typeof section.props === 'object' && section.props !== null ? (section.props as Record<string, unknown>) : {},
        },
      ]
    }),
    settings: {
      domain: text(settings, 'domain'),
      /* §3.181, FB-954 - `safeAsset` here is load-bearing rather than defensive: this is the first site
         setting that reaches the DOM as a **URL**, written into a `link.href`, where the others become a CSS
         variable or a `data-` attribute. `logo` carried the same call and was read by nothing. */
      faviconUrl: safeAsset(settings.faviconUrl),
      bodyFont: oneOf(text(settings, 'bodyFont') ?? text(settings, 'font'), FONTS),
      headingFont: oneOf(text(settings, 'headingFont') ?? text(settings, 'font'), FONTS),
      primaryColor: text(settings, 'primaryColor'),
      secondaryColor: text(settings, 'secondaryColor'),
      theme: oneOf(text(settings, 'theme'), THEMES),
      radius: oneOf(text(settings, 'radius'), RADII),
    },
    publishedAt: text(row, 'publishedAt'),
  }
}

/** The value when `index.css` has rules for it, else null - §3.166. */
function oneOf(value: string | null, allowed: readonly string[]): string | null {
  return value !== null && allowed.includes(value) ? value : null
}

/**
 * An asset URL from admin-authored settings: `http(s)` or a relative path, nothing else.
 *
 * The same rule the customer app applies to `about_us.linkUrl` and the homepage banner. A `javascript:`
 * URL in a logo field is not a logo.
 */
export function safeAsset(raw: unknown): string | null {
  if (typeof raw !== 'string' || raw.trim() === '') return null
  const value = raw.trim()
  try {
    const url = new URL(value)
    return url.protocol === 'https:' || url.protocol === 'http:' ? url.toString() : null
  } catch {
    return value.startsWith('/') ? value : null
  }
}

/**
 * A CTA target: an on-page anchor, a `mailto:`, or something `safeAsset` accepts.
 *
 * **`safeAsset` alone would drop all three of the owner's buttons.** Their hero points at `#features`,
 * their CTA at `mailto:pizzamajstro@gmail.com` and their secondary at `https://hk.toxik.space`; the
 * asset rule only admits `http(s)` and paths beginning `/`. A link is not an asset, and conflating the
 * two is how a fix for one defect introduces another.
 */
export function safeAnchorOrAsset(raw: string): string | null {
  const value = raw.trim()
  if (value.startsWith('#')) return value
  if (value.startsWith('mailto:') || value.startsWith('tel:')) return value
  const asset = safeAsset(value)
  if (asset !== null) return asset
  /* FB-815, §3.151 - **a domain typed without a scheme is what the operator meant.** `www.pizzamajstro.sk` is
     how most people write a URL, and `safeAsset` refused it: `new URL` throws with no scheme and it does not
     begin `/`. The CTA then fell back to `#`, so a primary button on the page that sells the product silently
     jumped to the top of it. Legacy is worse rather than better here - it writes the raw value, which the
     browser resolves against the current path and 404s.
     **The ordering is NOT what keeps this safe, and the first draft of this comment said it was.** It claimed a
     rejected scheme never reaches the retry because `safeAsset` returns null above - true, and it proves nothing:
     swapping the two branches leaves all 95 cases green, measured. What carries the guarantee is the **dot** in
     `schemeless`: prefixing `https://` makes the rejected scheme the *hostname*, and no script-executing scheme
     has a dot in it, so `javascript:alert(1)` is refused by the retry on its own terms. `data://x.y/z` parses to
     the hostname `data` for the same reason. So the dot is doing double duty - telling a host from prose, and
     refusing a scheme - and the assertion below pins the second job as well as the first. */
  return schemeless(value)
}

/**
 * A bare host, read as `https://`, or null.
 *
 * <p>The dot is the whole test, and it answers two questions at once: it separates `www.pizzamajstro.sk` from a
 * label somebody typed into the wrong box, **and it refuses a scheme this code does not want** - `javascript:x`
 * prefixed with `https://` reads as the host `javascript`, which has no dot. Whitespace is refused for the first
 * reason: a hostname has none, and a sentence does.
 */
function schemeless(value: string): string | null {
  if (value === '' || /\s/.test(value)) return null
  try {
    const guessed = new URL(`https://${value}`)
    return guessed.hostname.includes('.') ? guessed.toString() : null
  } catch {
    return null
  }
}

/**
 * The services a video section may be embedded from - `SAAS.md` §3.178, owner question #71.
 *
 * **An allow-list, because legacy has none.** Its `VideoSection` writes `<iframe src={embedUrl}>` after two
 * `String.replace` calls, so any origin at all is framed on the owner's public site. #71's own recommendation is
 * *"a named list of hosts (YouTube, Vimeo, and anything you actually use)"*, and what is still waiting on the
 * owner is **which extra services** - so a third host is one entry here rather than a decision.
 *
 * **Not routed through `safeAnchorOrAsset`, and that is the point.** That function is deliberately permissive -
 * a bare host, a `mailto:`, a `#anchor` - because a CTA's target is whatever the operator meant. An embed is the
 * opposite question: the browser will **execute** whatever is framed, so the default has to be refusal. **Two URL
 * rules with opposite defaults must not share a function.**
 */
const VIDEO_HOSTS = ['youtube.com', 'youtu.be', 'vimeo.com'] as const

/** `a.b.youtube.com` counts, `notyoutube.com` does not - a suffix match on a label boundary. */
function hostAllowed(hostname: string): (typeof VIDEO_HOSTS)[number] | null {
  const host = hostname.toLowerCase()
  for (const allowed of VIDEO_HOSTS) {
    if (host === allowed || host.endsWith(`.${allowed}`)) return allowed
  }
  return null
}

/** Seconds out of YouTube's `t`, which it writes as `90`, `90s` or `1m30s`. */
function startSeconds(raw: string | null): number | null {
  if (raw === null || raw === '') return null
  if (/^\d+s?$/.test(raw)) return Number(raw.replace('s', ''))
  const parts = /^(?:(\d+)h)?(?:(\d+)m)?(?:(\d+)s)?$/.exec(raw)
  if (parts === null) return null
  const [, hours, minutes, seconds] = parts
  const total = Number(hours ?? 0) * 3600 + Number(minutes ?? 0) * 60 + Number(seconds ?? 0)
  return total > 0 ? total : null
}

/**
 * An allow-listed video page or player URL, resolved to its embeddable form, or null.
 *
 * **It fixes two faults legacy has as well as adding the host check.** Legacy's
 * `.replace("youtube.com/watch?v=", "youtube.com/embed/")` turns `watch?v=ID&t=30` into `embed/ID&t=30`, which is
 * **not a URL** - the first parameter needs a `?` - so a timestamped link silently fails; the id is parsed here
 * and the start time re-attached. And legacy has **no Vimeo handling at all**, so a `vimeo.com/123` link frames
 * the Vimeo *page* rather than the player.
 */
export function videoEmbed(raw: string): string | null {
  const value = raw.trim()
  if (value === '' || /\s/.test(value)) return null

  let url: URL
  try {
    url = new URL(value.includes('://') ? value : `https://${value}`)
  } catch {
    return null
  }
  if (url.protocol !== 'https:' && url.protocol !== 'http:') return null

  const host = hostAllowed(url.hostname)
  if (host === null) return null

  const path = url.pathname.replace(/^\/+|\/+$/g, '')

  if (host === 'youtu.be') {
    const id = path.split('/')[0] ?? ''
    return youtubeEmbed(id, startSeconds(url.searchParams.get('t')))
  }
  if (host === 'youtube.com') {
    if (path === 'watch') {
      return youtubeEmbed(url.searchParams.get('v') ?? '', startSeconds(url.searchParams.get('t')))
    }
    // `/embed/ID` is already the player, and `/shorts/ID` and `/live/ID` are the same video by another route.
    const [first, second] = path.split('/')
    if ((first === 'embed' || first === 'shorts' || first === 'live') && second !== undefined) {
      return youtubeEmbed(second, startSeconds(url.searchParams.get('start') ?? url.searchParams.get('t')))
    }
    return null
  }

  // Vimeo: `vimeo.com/123456`, and `player.vimeo.com/video/123456` which is already the player.
  const digits = /(\d{6,})/.exec(path)
  if (digits === null) return null
  return `https://player.vimeo.com/video/${digits[1]}`
}

/** `https://www.youtube.com/embed/ID`, with `?start=` when the link carried a timestamp. */
function youtubeEmbed(id: string, start: number | null): string | null {
  // YouTube ids are 11 characters of the URL-safe alphabet; anything else is not an id we should frame.
  if (!/^[A-Za-z0-9_-]{11}$/.test(id)) return null
  const query = start === null ? '' : `?start=${String(start)}`
  return `https://www.youtube.com/embed/${id}${query}`
}

export function isRenderable(section: Section): boolean {
  return (RENDERABLE as readonly string[]).includes(section.type)
}

export function drawable(page: PublishedPage): Section[] {
  return page.sections.filter(isRenderable)
}

/** Types the editor published that this build cannot draw. Zero today. */
export function skipped(page: PublishedPage): string[] {
  return page.sections.filter((section) => !isRenderable(section)).map((section) => section.type)
}

/** A string prop, or `null` — every section's props are free-form JSON the editor writes. */
export function prop(section: Section, key: string): string | null {
  const value = section.props[key]
  return typeof value === 'string' && value.trim() !== '' ? value.trim() : null
}

/**
 * A string out of one of a list's entries.
 *
 * `String(value)` on an object renders `[object Object]`, and this is a public page — the editor writes
 * these blobs by hand and a nested object where a string was expected is not a hypothetical.
 */
export function field(entry: Record<string, unknown>, key: string): string {
  const value = entry[key]
  if (typeof value === 'string') return value
  if (typeof value === 'number' && Number.isFinite(value)) return String(value)
  return ''
}

export function propList(section: Section, key: string): Record<string, unknown>[] {
  const value = section.props[key]
  return Array.isArray(value) ? (value as Record<string, unknown>[]) : []
}

/**
 * The first of several names the editor might have written a field under.
 *
 * **Every one of these lists starts with the name the server actually sends**, measured off
 * `__fixtures__/home.json`. The alternatives are legacy's own names for the same field, kept because
 * the editor writes free-form JSON and an older published page may still carry them - a page published
 * in 2025 and never re-saved is exactly the case a 1:1 port has to survive.
 *
 * SAAS.md §4.1 is the entry this exists to satisfy: the first port read `ctaLabel`, which no payload
 * has ever contained, and drew nothing.
 */
export function anyProp(section: Section, keys: readonly string[]): string | null {
  for (const key of keys) {
    const found = prop(section, key)
    if (found !== null) return found
  }
  return null
}

/** The same, for a field inside one of a list's entries. */
export function anyField(entry: Record<string, unknown>, keys: readonly string[]): string {
  for (const key of keys) {
    const found = field(entry, key)
    if (found.trim() !== '') return found
  }
  return ''
}

/** A list under whichever of several keys the payload used. `pricing` sends `tiers`, not `plans`. */
export function anyList(section: Section, keys: readonly string[]): Record<string, unknown>[] {
  for (const key of keys) {
    const found = propList(section, key)
    if (found.length > 0) return found
  }
  return []
}

/**
 * Field names per section, server name first. **This is the contract §4.1 asks for**, in one place so
 * a reader can check it against the fixture rather than hunting through JSX.
 */
export const FIELDS = {
  ctaText: ['ctaText', 'ctaLabel'],
  ctaHref: ['ctaHref'],
  secondaryCtaText: ['secondaryCtaText', 'secondaryCtaLabel'],
  secondaryCtaHref: ['secondaryCtaHref'],
  eyebrow: ['eyebrow'],
  imageUrl: ['imageUrl', 'image', 'src'],
  featureBody: ['body', 'text', 'description'],
  featureTitle: ['title', 'name'],
  logoLabel: ['label', 'name', 'alt'],
  logoHref: ['href', 'url'],
  planName: ['name', 'title'],
  planPrice: ['monthlyPrice', 'price'],
  planNote: ['description', 'text'],
  quote: ['quote', 'text', 'body'],
  quoteAuthor: ['author', 'name'],
  quoteRole: ['role', 'company'],
  question: ['q', 'question'],
  answer: ['a', 'answer'],
  /* §3.130 - legacy's `BannerSection` reads its message from `text`, not `title`: its banner is a strip with
     no heading, so `SectionView`'s chrome draws nothing and this is the whole content. */
  bannerText: ['text', 'body'],
  /* §3.131 - `SplitSection`'s paragraph. Read by the split's own case rather than by `SectionView`, which is the
     judgement: aliasing `subtitle` to accept `body` would change how all fifteen other types read theirs. */
  splitBody: ['body', 'text'],
  /* §3.130 - `QuoteSection`'s own fourth field. `imageUrl` last so a hand-written page that used the generic
     name still draws a face. */
  quoteAvatar: ['avatarUrl', 'avatar', 'imageUrl'],
  /* §3.130 - `SpacerSection` reads `size` off the props, not off `style`, and falls back to its middle value. */
  spacerSize: ['size'],
  /* §3.129 - legacy's `StatsSection` reads `{value, label, suffix}` off each entry; the aliases are the
     readable spellings a hand-written page might use. Server name first, as every entry here is. */
  statValue: ['value', 'figure'],
  statLabel: ['label', 'caption'],
  statSuffix: ['suffix', 'unit'],
  caption: ['caption', 'alt'],
  alt: ['alt', 'caption'],
  footerBrand: ['brand', 'title'],
  footerTagline: ['tagline', 'subtitle'],
  footerCopyright: ['copyright', 'text'],
  linkLabel: ['label', 'title'],
  linkHref: ['href', 'url'],
  /* §3.178 - legacy's `VideoSection` destructures `embedUrl`; the aliases are the readable spellings a
     hand-written page might use. Server name first, as every entry here is. */
  videoUrl: ['embedUrl', 'videoUrl', 'url'],
} as const

/** Lists per section type, server name first. */
export const LISTS = {
  items: ['items'],
  plans: ['tiers', 'plans'],
  columns: ['columns'],
  links: ['links'],
} as const

/**
 * An anchor a hero or CTA button can scroll to.
 *
 * Legacy slugifies defensively - *"so a stray space or accented char doesn't break #-links"* - and
 * wraps the section in a `<div id=…>`. The owner's `cta` section sets `anchorId: "demo"` and their
 * hero button points at `#demo`, so without this the button scrolls nowhere even once it renders.
 */
export function anchorOf(section: Section): string | null {
  const raw = prop(section, 'anchorId')
  if (raw === null) return null
  const slug = raw
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9_-]+/g, '-')
    .replace(/^-+|-+$/g, '')
  return slug === '' ? null : slug
}

/**
 * `siteSettings` as the custom properties and attributes the page is drawn with - FB-479, §4.2.
 *
 * **Absent means "keep the design system's default", never "write empty".** Setting `--brand` to an
 * empty string would break every token that falls back through it, which is worse than not setting it.
 *
 * **`theme` and `radius` are read and applied since §3.166** - see {@link siteAttributes}. This paragraph used
 * to list them among the settings *"read if they ever arrive"*, and they were not: `SiteSettings` carried
 * neither, so a backend that started sending them would have been ignored in silence while this comment said
 * the opposite.
 *
 * **This paragraph used to end by calling `bodyFont`, `headingFont` and `faviconUrl` legacy settings AGOS's
 * backend does not send, with §4.2's rule about rendering the default rather than inventing a control.** All
 * three are settable and read now - `faviconUrl` by §3.181 and the two faces by §3.182 - and the sentence was
 * never quite right about the mechanism either: the backend stores and serves the blob whole, with no key
 * whitelist, so what was missing was a control rather than a wire. **Corrected rather than deleted, because it
 * is the sentence a later reader would use to justify not building them.**
 */
/**
 * The data attributes the page is drawn with - §3.166.
 *
 * **Legacy sets these and AGOS set neither**, so the owner's public site was dark permanently and a franchise
 * whose brand is light had no way to say so on the page that sells their business. `index.css` carries the
 * rules for both, ported from legacy's.
 *
 * **An absent value is omitted rather than written empty**, for `cssVariables`' own reason about `--brand`: a
 * `data-theme=""` matches no rule and reads as a value somebody set.
 */
export function siteAttributes(settings: SiteSettings): Record<string, string> {
  const attributes: Record<string, string> = {}
  if (settings.theme !== null) attributes['data-theme'] = settings.theme
  if (settings.radius !== null) attributes['data-radius'] = settings.radius
  return attributes
}

export function cssVariables(settings: SiteSettings): Record<string, string> {
  const vars: Record<string, string> = {}
  if (settings.primaryColor !== null) vars['--brand'] = settings.primaryColor
  if (settings.secondaryColor !== null) vars['--brand-secondary'] = settings.secondaryColor
  /* §3.182, FB-956 - two faces rather than one stack written twice.
   *
   * **The comment that was here was true and was the defect**: *"A name outside that set still gets a stack,
   * because the browser falls through to `system-ui` on its own."* The fall-through is what made a family the
   * site cannot draw look like a choice that took. `publishedPageOf` refuses one now, so anything reaching
   * here is a family `index.css` has loaded. */
  const stack = (family: string): string => `'${family}', system-ui, sans-serif`
  if (settings.bodyFont !== null) vars['--font-body'] = stack(settings.bodyFont)
  if (settings.headingFont !== null) vars['--font-display'] = stack(settings.headingFont)
  return vars
}

/**
 * The `<title>` and meta description to write into the document.
 *
 * §4 says SEO matters here, and the fallbacks are the ones already in `index.html` — a page that fails
 * to load must not leave a crawler with an empty title.
 */
export function documentMeta(page: PublishedPage | null): { title: string; description: string } {
  if (page === null || page.title === '') {
    return { title: tt('PizzaMajstro'), description: tt('Systém pre reštaurácie.') }
  }
  return { title: page.title, description: page.description }
}

/** Said in place of the page when there is not one. A 404 here means *not published*, not *broken*. */
export function missingNote(): string {
  return tt('Táto stránka zatiaľ nie je zverejnená.')
}

/**
 * What a visitor reads when the site's own read did not answer - FB-825, §3.154.
 *
 * **Separate from `missingNote` on purpose.** That one is a fact about the owner's business; this one is a fact about
 * the request, and `if (isError || page === null)` was telling every visitor the first when the second was true.
 */
export function unreachableNote(): string {
  return tt('Stránku sa teraz nepodarilo načítať. Skúste to prosím znova - nie je to chyba na vašej strane.')
}

/**
 * A section's `style` object - SAAS.md §4.4, FB-489.
 *
 * **This is §4.1's rule a second time.** `style` is a field like `ctaText`, and it was read by
 * nothing: the owner's hero carries `{bg: "dark", align: "center", titleSize: "xl"}`, their features
 * `{layout: "cards"}`, their gallery `{columns: 3}` and their pricing
 * `{highlight: "pizzamajstro"}`. The §12.3 pair is what made it visible - AGOS was left-aligned and
 * body-sized where legacy centres and goes display-sized, because nothing asked.
 */
export function styleOf(section: Section): Record<string, unknown> {
  const raw = section.props.style
  return typeof raw === 'object' && raw !== null && !Array.isArray(raw)
    ? (raw as Record<string, unknown>)
    : {}
}

function styleWord(style: Record<string, unknown>, key: string, fallback: string): string {
  const value = style[key]
  return typeof value === 'string' && value.trim() !== '' ? value.trim() : fallback
}

/** Legacy's `alignClass`, unchanged. `left` is its default and so is this one's. */
export function alignClass(style: Record<string, unknown>): string {
  const align = styleWord(style, 'align', 'left')
  if (align === 'center') return 'text-center mx-auto'
  if (align === 'right') return 'text-right ml-auto'
  return 'text-left'
}

/** Legacy's `paddingClass`, unchanged - `normal` unless the editor said otherwise. */
export function paddingClass(style: Record<string, unknown>): string {
  const padding = styleWord(style, 'padding', 'normal')
  if (padding === 'tight') return 'py-10 sm:py-14'
  if (padding === 'loose') return 'py-24 sm:py-36 lg:py-44'
  return 'py-20 sm:py-28'
}

/**
 * Legacy's `bgClass`, unchanged including the empty default.
 *
 * A value this build does not recognise returns nothing rather than guessing, which is legacy's own
 * `default:` arm - a section with an unknown ground inherits the page's.
 */
export function bgClass(style: Record<string, unknown>): string {
  switch (styleWord(style, 'bg', '')) {
    case 'dark':
      return 'bg-slate-950'
    case 'light':
      return 'bg-slate-50 text-slate-900'
    case 'muted':
      return 'bg-slate-900/50'
    case 'gradient':
      return 'bg-gradient-to-b from-orange-500/10 via-transparent to-transparent'
    case 'brand':
      return 'brand-bg text-white'
    default:
      return ''
  }
}

/**
 * The panel legacy wraps a section's whole content in - §4.4.
 *
 * **Only `cta` has one, and it has to be outside the heading**, which is why this is a `SectionView`
 * concern rather than a renderer's: legacy's `CtaSection` puts the title, the subtitle and the buttons
 * all inside one bordered gradient card. AGOS drew them as bare text on the page.
 *
 * **The orange is legacy's own literal and is ported as one.** Its button beside it uses `brand-bg` -
 * the franchise's colour - so legacy is internally inconsistent here: a purple-branded site gets a
 * warm-tinted panel with a purple button. §4 says the public design stays exactly as-is, and that
 * includes the parts that look like a mistake; changing it would be a redesign nobody asked for.
 */
export function panelClass(type: string): string {
  if (type !== 'cta') return ''
  return 'rounded-3xl border border-orange-500/20 bg-gradient-to-br from-orange-500/20 via-orange-500/5 to-transparent p-8 sm:p-12 lg:p-16'
}

/**
 * Which sections legacy centres, and which it leaves left - §4.4.
 *
 * **Read off legacy's own renderers, one at a time, not guessed from the pair.** `FaqSection`,
 * `TestimonialsSection`, `GallerySection`, `CtaSection`, `LogosSection` and `PricingSection` all put
 * `text-center` on their heading block unconditionally; `FeaturesSection` does not. So this is not a
 * style the editor chose - it is the section's own layout, and AGOS was left-aligning all of them.
 *
 * The editor's `style.align` still wins where it set one: the owner's hero carries `center`, and a
 * per-type default must not override an explicit choice.
 */
const CENTRED_SECTIONS = new Set(['pricing', 'faq', 'testimonials', 'gallery', 'cta', 'logos'])

export function headingAlignClass(type: string, style: Record<string, unknown>): string {
  if (typeof style.align === 'string' && style.align.trim() !== '') return alignClass(style)
  return CENTRED_SECTIONS.has(type) ? 'text-center mx-auto' : 'text-left'
}

/**
 * `logos` has a label, not a heading - `text-xs uppercase tracking-[0.2em] text-slate-500` in legacy.
 *
 * AGOS drew *Dôverujú nám* at display size, which is a different thing on the page: legacy's is a
 * quiet trust strip above the names, not a section title competing with *Plány, ktoré rastú s vami*.
 */
export function headingClass(type: string, style: Record<string, unknown>): string {
  if (type === 'logos') return 'text-xs uppercase tracking-[0.2em] text-slate-500'
  return titleClass(style)
}

/**
 * The heading scale.
 *
 * **Two scales, because legacy has two.** Its `HeroSection` carries its own - `xxl`, `xl` (its
 * default) and `lg` - while every other section hard-codes `text-3xl sm:text-4xl lg:text-5xl`. The
 * values here are legacy's, read one at a time rather than interpolated: `xl` is
 * `text-4xl sm:text-5xl lg:text-7xl`, and the first version of this guessed `lg:text-6xl`.
 */
export function titleClass(style: Record<string, unknown>): string {
  const size = styleWord(style, 'titleSize', '')
  if (size === 'xxl') return 'text-5xl font-black leading-[1.05] tracking-tight sm:text-7xl lg:text-8xl'
  if (size === 'xl') return 'text-4xl font-black leading-[1.05] tracking-tight sm:text-5xl lg:text-7xl'
  if (size === 'lg') return 'text-3xl font-black leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl'
  // Every section that is not the hero. Legacy hard-codes this and takes no `titleSize` for them.
  return 'text-3xl font-black tracking-tight sm:text-4xl lg:text-5xl'
}

/**
 * The hero's title, with the last word in the brand gradient - §4.4.
 *
 * **Legacy's own split, and it is deterministic**, which is the only reason it is portable: the title
 * is split on whitespace and the **last** word gets `gradient-text`. That is why *"Autonómny operačný
 * systém pre moderné gastro."* renders with `gastro.` in the franchise's colours, and AGOS rendered
 * the whole line in one weight.
 *
 * Returned as words rather than markup so the renderer stays in the component and this stays
 * testable: the interesting part is *which* word, not the span around it.
 */
export function heroTitleWords(title: string): Array<{ word: string; gradient: boolean }> {
  const words = title.trim().split(/\s+/).filter((word) => word !== '')
  return words.map((word, index) => ({ word, gradient: index === words.length - 1 }))
}

/** How many columns a grid section asks for, clamped to what a layout can hold. */
export function columnsClass(style: Record<string, unknown>, fallback = 3): string {
  /* §3.132 - **a number or a numeric string**, because the two writers disagree and both are legitimate.
     Legacy's editor writes `Number(e.target.value)`; this editor's `sectionStyled` writes every style value as a
     string. Reading only a number meant a `columns` select here would write `"4"`, the grid would stay at three,
     and the control would look like it worked - and no test on the owner's fixture could catch it, because their
     data is legacy's and therefore numeric. The tolerance lives here rather than in the writer: putting type
     knowledge about one key into the generic writer makes the next numeric key somebody's turn to remember. */
  const raw = style.columns
  const typed =
    typeof raw === 'number' ? raw : typeof raw === 'string' && raw.trim() !== '' ? Number(raw) : Number.NaN
  const columns = Number.isFinite(typed) ? Math.round(typed) : fallback
  if (columns <= 1) return 'grid-cols-1'
  if (columns === 2) return 'grid-cols-1 sm:grid-cols-2'
  if (columns >= 4) return 'grid-cols-2 sm:grid-cols-4'
  return 'grid-cols-1 sm:grid-cols-3'
}

/**
 * A monthly price as legacy prints it - §4.4, and the one to fix first.
 *
 * **AGOS printed `149.00`.** The raw `monthlyPrice` string, on the page that sells the product, with
 * no currency at all and in a format no Slovak page uses. Legacy prints `€{Number(price).toFixed(0)}`
 * with `/ mesiac` beside it, and whole euros is deliberate: a tier is quoted at 149, not 149,00.
 *
 * Anything that is not a number comes back empty rather than as itself - a price that cannot be read
 * is not a price, and printing the raw string is how `149.00` got onto the page.
 */
export function monthlyPrice(raw: string): string {
  const value = Number(String(raw).replace(',', '.'))
  if (!Number.isFinite(value) || value <= 0) return ''
  return `€${Math.round(value)}`
}

/**
 * Whether this tier is the highlighted one.
 *
 * Legacy: the slug named by `style.highlight`, and failing that the `advanced` tier or whichever is
 * second. The fallback is ported including its oddity, because a highlight that moved when the port
 * shipped would have been a visible change nobody asked for.
 *
 * **This javadoc used to say the owner's `pizzamajstro` names no tier of theirs and the fallback
 * decides. That was false - §3.133.** Their pricing carries three tiers with slugs `basic`,
 * `pizzamajstro` and `standard`, so the first branch matches and the fallback is never reached. The
 * claim survived because their `pizzamajstro` tier is **also the middle one**, so both rules select the
 * same tier on the only page anybody has, and the test asserted only that exactly one is featured.
 * **A fixture cannot separate two rules that agree on it**; `sections.test.tsx` now drives a section
 * where they disagree.
 */
export function isFeaturedTier(
  style: Record<string, unknown>,
  slug: string,
  index: number,
): boolean {
  const highlight = styleWord(style, 'highlight', '')
  if (highlight !== '') return slug === highlight
  return slug === 'advanced' || index === 1
}

/** The product name the share card and the structured data are issued under. */
export const APP_NAME = 'PizzaMajstro'

/**
 * The social tags to rewrite from the page that loaded - FB-480, §4.3.
 *
 * **Keyed by the selector rather than by a name**, because `og:*` is a `property` and `twitter:*` is a
 * `name`, and writing one as the other produces a tag no crawler reads. Legacy makes that distinction
 * and it is the only reason this is a list of pairs instead of a record.
 *
 * A tag whose value would be empty is **omitted, not blanked**: an `og:description=""` is worse for a
 * share card than the static fallback the head already carries.
 */
export function socialMeta(page: PublishedPage, url: string, image: string): Array<[string, string]> {
  const tags: Array<[string, string]> = [
    ["meta[property='og:title']", page.title],
    ["meta[property='og:url']", url],
    ["meta[property='og:image']", image],
    ["meta[property='og:site_name']", APP_NAME],
    ["meta[name='twitter:title']", page.title],
    ["meta[name='twitter:image']", image],
  ]
  /* §3.179, FB-951 - **the card type follows the image, and that is not cosmetic.** `summary_large_image` asks
     the platform for a large card and therefore for an image, so declaring it with none renders as a **broken**
     card rather than a text one. The static head declares `summary`; this upgrades it only when there is
     something to show. */
  if (image !== '') {
    tags.push(["meta[name='twitter:card']", 'summary_large_image'])
  }
  if (page.description !== '') {
    tags.push(["meta[property='og:description']", page.description])
    tags.push(["meta[name='twitter:description']", page.description])
  }
  return tags.filter(([, value]) => value !== '')
}

/**
 * The JSON-LD block, rebuilt from the published page. Legacy's shape exactly, including `price: "0"` -
 * the free trial the page advertises, not a claim that the product is free.
 */
export function structuredData(page: PublishedPage, url: string): Record<string, unknown> {
  const data: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: APP_NAME,
    applicationCategory: 'BusinessApplication',
    operatingSystem: 'Web, iOS, Android',
    url,
    inLanguage: 'sk-SK',
    offers: {
      '@type': 'Offer',
      priceCurrency: 'EUR',
      price: '0',
      availability: 'https://schema.org/InStock',
    },
  }
  // Legacy sends `description: undefined` when the page has none, which `JSON.stringify` drops. Same
  // outcome, said in a way that does not rely on that.
  if (page.description !== '') data.description = page.description
  return data
}

/**
 * The share image. `siteSettings` has no `ogImage` field on AGOS's endpoint, so this is legacy's
 * fallback branch - the one it takes in production too, since the owner has never set one.
 */
export function shareImage(page: PublishedPage, origin: string): string {
  for (const section of page.sections) {
    // The hero first, because it is the image the page itself leads with.
    if (section.type === 'hero') {
      const hero = absolute(anyProp(section, FIELDS.imageUrl), origin)
      if (hero !== null) return hero
    }
  }
  for (const section of page.sections) {
    if (section.type === 'image') {
      const standalone = absolute(anyProp(section, FIELDS.imageUrl), origin)
      if (standalone !== null) return standalone
    }
    if (section.type === 'gallery') {
      for (const entry of anyList(section, LISTS.items)) {
        const shot = absolute(anyField(entry, FIELDS.imageUrl), origin)
        if (shot !== null) return shot
      }
    }
  }
  /* **An empty string rather than a constant, and that is the fix.** `socialMeta` ends
     `filter(([, value]) => value !== '')`, so this omits `og:image` and `twitter:image` instead of naming a file
     that is not there - the mechanism was already present and the hard-coded `/og-image.png` is what stopped it
     ever being reached. A card with no image is honest; a card whose image answers `text/html` is not. */
  return ''
}

/** An uploaded path made absolute, or null - `safeAsset`'s rule, which is the one for an upload. */
function absolute(raw: string | null, origin: string): string | null {
  const asset = raw === null ? null : safeAsset(raw)
  if (asset === null) return null
  return asset.startsWith('/') ? `${origin}${asset}` : asset
}
